import {
  AdvicePriceCalculationResult,
  AdviceProductChangeType,
  InsuranceAdvice,
  PurchaseRecommendation,
} from "Store/advice/interfaces";
import {
  getCorrectAdviceValues,
  mapFormToProductPriceRequest,
} from "Store/advice/productPriceRequestMappers";
import {
  getAdvicePlotRange,
  getAdviceRestrictions,
  getMonthlyPrice,
  getPurchaseFromAdvisorCardMessage,
  replaceUserSupportNumberInText,
} from "./utils";

import { StyledAlert } from "App/global-style";
import CollapsibleContainer from "Components/CollapsibleContainer";
import { DetailsCardWrapper } from "Components/InsuranceDetailsCard/styled";
import { VALUE_TYPE } from "Components/NumberValue";
import { ProductBackendName } from "Features/CheckPrice/Products/productTypes";
import { customerChangeProductUpdate } from "Store/advice/slice";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { usePartnerId } from "Store/partners/partnerHooks";
import { FormikProps } from "formik";
import React from "react";
import { useDispatch } from "react-redux";
import { AdviceProductDetailsFormValues } from "../../ProductDetails/FormElements/interfaces";
import { useIsAdvicePriceLoading } from "../hooks/stateSelectors";
import { mapInsuranceNameToAdviceCoverageName } from "../utils";
import { AdviceCardHeader } from "./AdviceCardHeader";
import { AdvicePlotCriticalIllness } from "./AdvicePlots/CriticalIllness/AdvicePlotCriticalIllness";
import { getAdviceTextsCriticalIllness } from "./AdviceTexts/getAdviceTexts";
import { AdvisorUwInfo } from "./AdvisorUwInfo";
import { AlreadyInsured } from "./AlreadyInsured";
import { DiscreteSlider } from "./DiscreteSlider";
import { ProductCoverages } from "./ProductCoverages";
import { usePriceStatus } from "./hooks";
import { AdvicePriceFormValues } from "./interfaces";
import { ContentWrapper } from "./styled";

const SLIDER_STEP = 100000;

export interface Props {
  formProps: FormikProps<AdvicePriceFormValues>;
  adviceProductDetailsFormValues: AdviceProductDetailsFormValues;
  insuranceAdvice: InsuranceAdvice;
  insurancePrice: AdvicePriceCalculationResult;
  adviceCard: any;
  lang: string;
}

export const CriticalIllnessAdviceCard = ({
  formProps,
  adviceProductDetailsFormValues,
  insuranceAdvice,
  insurancePrice,
  adviceCard,
  lang,
}: Props): React.ReactElement | null => {
  const dispatch = useDispatch();
  const isAdvisor = useIsAdvisorSelector();
  const partnerId = usePartnerId();
  const { adviceValue, minAdviceValue, maxAdviceValue } =
    getCorrectAdviceValues(insuranceAdvice.singlePayment, isAdvisor);
  const selected = formProps.values.criticalIllnessCheckedToPurchase;
  const currentSliderValue = formProps.values.criticalIllnessInsuredSumValue;
  const {
    priceIsChanging,
    setPriceIsChanging,
    priceIsLoading,
    setPriceIsLoading,
  } = usePriceStatus();
  const isLoading = useIsAdvicePriceLoading();
  const insuranceType: string = adviceCard.insuranceType[lang];
  const shouldPurchaseFromAdvisor =
    !isAdvisor &&
    insuranceAdvice.singlePayment.recommendationConfiguration.recommendation ===
      PurchaseRecommendation.PURCHASE_FROM_ADVISOR;

  const handleSetCurrentSliderValue = (value: number): void => {
    formProps.setFieldValue("criticalIllnessInsuredSumValue", value);
    setPriceIsChanging(true);
  };

  const handleCustomerChange = (
    changeType: AdviceProductChangeType,
    changeValue: number | boolean,
  ): void => {
    if (changeType === AdviceProductChangeType.PRODUCT_SLIDER) {
      setPriceIsChanging(false);
      setPriceIsLoading(true);
    }
    const priceCalculationRequest = mapFormToProductPriceRequest(
      adviceProductDetailsFormValues,
      INSURANCE_NAME_VALUE.CRITICAL_ILLNESS,
    );
    const finalSliderValue =
      changeType === AdviceProductChangeType.PRODUCT_SLIDER
        ? (changeValue as number)
        : currentSliderValue;
    const finalCheckedValue =
      changeType === AdviceProductChangeType.PRODUCT_CHECKBOX
        ? (changeValue as boolean)
        : selected;
    dispatch(
      customerChangeProductUpdate({
        adviceProductChangeType: changeType,
        insuranceName: INSURANCE_NAME_VALUE.CRITICAL_ILLNESS,
        productCheckedToPurchase: finalCheckedValue,
        sliderValue: finalSliderValue,
        priceCalculationRequest: {
          ...priceCalculationRequest,
          data: {
            ...priceCalculationRequest.data,
            insuredSum: finalSliderValue,
          },
        },
      }),
    );
    if (changeType === AdviceProductChangeType.PRODUCT_SLIDER) {
      formProps.setFieldValue(
        "criticalIllnessInsuredSumValue",
        changeValue as number,
      );
    }
  };

  if (!adviceProductDetailsFormValues) {
    return null;
  }
  const income = adviceProductDetailsFormValues.income;
  const restrictions = getAdviceRestrictions(insuranceAdvice);
  const plotRange = getAdvicePlotRange(
    isAdvisor,
    income,
    restrictions.maxCustomer,
  );

  return (
    <div className="AdviceCard_CRITICAL_ILLNESS">
      <DetailsCardWrapper
        key={insuranceType}
        adviceDetailsCard
        selected={selected}
      >
        <AdviceCardHeader
          insuranceId="criticalIllnessCheckedToPurchase"
          insuranceType={insuranceType}
          price={getMonthlyPrice(insurancePrice)}
          handleCustomerChange={handleCustomerChange}
          priceIsChanging={priceIsChanging}
          priceIsLoading={priceIsLoading}
        />
        <ContentWrapper className="AdviceCardContent">
          {getAdviceTextsCriticalIllness(
            isAdvisor,
            insuranceAdvice,
            adviceCard.adviceTexts,
            lang,
          )}
          <CollapsibleContainer isOpen={selected}>
            <AdvicePlotCriticalIllness
              income={income}
              payout={currentSliderValue}
              valueType={VALUE_TYPE.NOK}
              advicePlot={adviceCard.advicePlot}
              lang={lang}
              range={plotRange}
            />
            <DiscreteSlider
              setCurrentSliderValue={handleSetCurrentSliderValue}
              handleCustomerChange={handleCustomerChange}
              step={SLIDER_STEP}
              min={restrictions.minPayment}
              minAdvice={minAdviceValue}
              maxAdvice={maxAdviceValue}
              max={
                isAdvisor
                  ? restrictions.maxProductManager
                  : restrictions.maxCustomer
              }
              adviceValue={adviceValue}
              defaultValue={currentSliderValue}
              adviceValueMarkLabel={
                shouldPurchaseFromAdvisor
                  ? adviceCard.sliderValueMarker.maxAmount[lang]
                  : adviceCard.sliderValueMarker.advicedAmount[lang]
              }
              valueType={VALUE_TYPE.NOK}
              disabled={isLoading}
              label={adviceCard.sliderValueMarker.advicedAmount[lang]}
            />
            {isAdvisor && (
              <AdvisorUwInfo
                insuranceName={INSURANCE_NAME_VALUE.CRITICAL_ILLNESS}
                sliderValue={currentSliderValue}
                restrictions={restrictions}
                uwInfoTexts={adviceCard.advisorTexts.uwInfoTexts}
                lang={lang}
              />
            )}
            {!isAdvisor &&
              !shouldPurchaseFromAdvisor &&
              currentSliderValue === restrictions.maxCustomer && (
                <StyledAlert
                  className="PurchaseFromAdvisorText"
                  title={replaceUserSupportNumberInText(
                    partnerId,
                    adviceCard.adviceUnavailableMessages.purchaseFromAdvisor
                      .userSupportNumber,
                    adviceCard.adviceUnavailableMessages.purchaseFromAdvisor
                      .maxPayoutCustomer[lang],
                    lang,
                  )}
                />
              )}
            {shouldPurchaseFromAdvisor && (
              <StyledAlert
                className="PurchaseFromAdvisorText"
                title={getPurchaseFromAdvisorCardMessage(
                  partnerId,
                  insuranceAdvice,
                  adviceCard.adviceUnavailableMessages.purchaseFromAdvisor,
                  lang,
                )}
              />
            )}
            <AlreadyInsured insuranceType={insuranceType} />
          </CollapsibleContainer>
          <ProductCoverages
            selected={selected}
            readableInsuranceType={insuranceType}
            backendInsuranceName={ProductBackendName.CRITICAL_ILLNESS}
            coverageGroup={mapInsuranceNameToAdviceCoverageName(
              INSURANCE_NAME_VALUE.CRITICAL_ILLNESS,
            )}
            description={adviceCard.adviceTexts.productCoveragesDesc[lang]}
          />
        </ContentWrapper>
      </DetailsCardWrapper>
    </div>
  );
};
